import packedManifest from '../shared/resource-packs.json';
import { registerPackedAsset } from './asset-delivery';
import { readResourcePackResponse, resourcePackPrefetchFor, type ResourcePackPrefetch } from './resource-pack-prefetch';

export const MAX_CONCURRENT_PACK_DOWNLOADS = 6;
export const PACK_DOWNLOAD_TIMEOUT_MS = 60_000;
export interface PackEntry { path: string; offset: number; length: number; type: string }
export interface ResourcePack { url: string; bytes: number; sha256: string; entries: PackEntry[] }
export interface ResourcePackManifest { schemaVersion: number; magic: string; headerBytes: number; packs: ResourcePack[] }

/** Exact encoded images/masks, independently cached and downloaded in parallel. */
export class ResourcePackLoader {
  private files = new Map<string, Blob>();
  private lookup = new Map<string, ResourcePack>();
  private loads = new Map<string, Promise<void>>();
  private completed = new Set<string>();
  private requested = new Set<string>();
  private failures = new Set<string>();
  private received = new Map<string, number>();
  private active = 0;
  private queued: Array<() => void> = [];
  private prefetch?: ResourcePackPrefetch;
  private unsubscribePrefetch?: () => void;
  onProgress?: () => void;

  constructor(private manifest: ResourcePackManifest = packedManifest) {
    if (manifest.schemaVersion !== 1 || manifest.magic !== 'DDTPACK1' || manifest.headerBytes !== 16) throw Error('Unsupported resource pack');
    for (const pack of manifest.packs) for (const entry of pack.entries) {
      if (this.lookup.has(entry.path)) throw Error('Duplicate packed resource');
      this.lookup.set(entry.path, pack);
    }
    this.prefetch = resourcePackPrefetchFor(manifest.packs);
    if (this.prefetch) {
      for (const pack of manifest.packs) this.requested.add(pack.url);
      this.unsubscribePrefetch = this.prefetch.subscribe(() => this.onProgress?.());
    }
  }

  has(path: string): boolean { return this.lookup.has(path); }
  preloadAll(): Promise<boolean> { return this.preload([...this.lookup.keys()]); }
  preload(paths: string[]): Promise<boolean> {
    const packs = new Set(paths.map(path => this.lookup.get(path)).filter((pack): pack is ResourcePack => Boolean(pack)));
    // An explicit new preload is the retry boundary. Every frame belonging to
    // a failed pack shares that failure instead of triggering another transfer.
    for (const pack of packs) if (!this.loads.has(pack.url)) this.failures.delete(pack.url);
    return Promise.allSettled([...packs].map(pack => this.load(pack))).then(results => results.every(result => result.status === 'fulfilled'));
  }
  async file(path: string): Promise<Blob | null> {
    const pack = this.lookup.get(path);
    if (!pack) return null;
    await this.load(pack);
    return this.files.get(path) ?? null;
  }
  status() {
    const packs = this.manifest.packs.filter(pack => this.requested.has(pack.url));
    const prefetched = packs.map(pack => [pack.url, this.prefetch?.records.get(pack.url)] as const)
      .filter(([, record]) => record && !record.consumed);
    return { totalPacks: packs.length, loadedPacks: this.completed.size,
      totalBytes: packs.reduce((sum, pack) => sum + pack.bytes, 0),
      downloadedBytes: packs.reduce((sum, pack) => sum + (this.received.get(pack.url) ?? this.prefetch?.records.get(pack.url)?.received ?? 0), 0),
      activeDownloads: this.active + prefetched.filter(([, record]) => record?.state === 'active').length,
      queuedDownloads: this.queued.length + prefetched.filter(([, record]) => record?.state === 'queued').length,
      failed: [...new Set([...this.failures, ...prefetched.filter(([, record]) => record?.failed).map(([url]) => url)])] };
  }

  private load(pack: ResourcePack): Promise<void> {
    if (this.completed.has(pack.url)) return Promise.resolve();
    const existing = this.loads.get(pack.url); if (existing) return existing;
    if (this.failures.has(pack.url)) return Promise.reject(Error('Resource pack needs retry'));
    this.requested.add(pack.url); this.failures.delete(pack.url);
    const prefetched = this.prefetch?.consume(pack.url, pack.bytes) ?? this.prefetch?.retry(pack.url, pack.bytes);
    if (prefetched) {
      this.received.delete(pack.url);
      this.unsubscribePrefetch ??= this.prefetch!.subscribe(() => this.onProgress?.());
    }
    const transfer = prefetched ?? this.schedule(() => this.download(pack));
    const loading = transfer.then(async bytes => {
      if (bytes.length !== pack.bytes || bytes.length < 16) throw Error('Truncated resource pack');
      const view = new DataView(bytes.buffer);
      if (new TextDecoder().decode(bytes.subarray(0, 8)) !== this.manifest.magic || view.getUint32(8, true) !== pack.entries.length || view.getUint32(12, true) !== 16) throw Error('Invalid resource pack header');
      const digest = await crypto.subtle.digest('SHA-256', bytes.buffer);
      if ([...new Uint8Array(digest)].map(byte => byte.toString(16).padStart(2, '0')).join('') !== pack.sha256) throw Error('Resource pack checksum mismatch');
      let end = 16;
      for (const entry of pack.entries) {
        if (!Number.isSafeInteger(entry.offset) || !Number.isSafeInteger(entry.length) || entry.length <= 0 || entry.offset !== end || entry.offset + entry.length > bytes.length) throw Error('Invalid resource pack bounds');
        end = entry.offset + entry.length;
      }
      if (end !== bytes.length) throw Error('Unexpected resource pack suffix');
      for (const entry of pack.entries) {
        const blob = new Blob([bytes.subarray(entry.offset, entry.offset + entry.length)], { type: entry.type });
        this.files.set(entry.path, blob);
        if (entry.type.startsWith('image/')) registerPackedAsset(entry.path, blob);
      }
      this.completed.add(pack.url); this.received.set(pack.url, bytes.length); this.onProgress?.();
    }).catch(error => { this.failures.add(pack.url); this.received.set(pack.url, 0); throw error; })
      .finally(() => {
        if (prefetched) {
          this.prefetch!.release(pack.url);
          if (!this.prefetch!.downloads.size) { this.unsubscribePrefetch?.(); this.unsubscribePrefetch = undefined; }
        }
        this.loads.delete(pack.url); this.onProgress?.();
      });
    this.loads.set(pack.url, loading);
    return loading;
  }

  private async download(pack: ResourcePack): Promise<Uint8Array<ArrayBuffer>> {
    this.received.set(pack.url, 0);
    const signal = AbortSignal.timeout(PACK_DOWNLOAD_TIMEOUT_MS);
    const response = await fetch(pack.url, { signal });
    return readResourcePackResponse(response, pack.bytes, length => { this.received.set(pack.url, length); this.onProgress?.(); }, signal);
  }

  private schedule<T>(work: () => Promise<T>): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      this.queued.push(() => {
        this.active++;
        void Promise.resolve().then(work).then(resolve, reject).finally(() => { this.active--; this.drain(); this.onProgress?.(); });
      });
      this.drain();
    });
  }
  private drain() {
    while (this.active < MAX_CONCURRENT_PACK_DOWNLOADS && this.queued.length) this.queued.shift()!();
  }
}
